import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, FileText, Trash2, ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { STATUSES, Select, inputCls, money, typeLabel, useApi } from "@/components/shop/common";
import { LaborEditor, PartsEditor } from "@/components/shop/LineEditors";
import MessageDialog from "@/components/shop/MessageDialog";
import TimePanel from "@/components/shop/TimePanel";
import { useAuth } from "@/context/AuthContext";

const Panel = ({ title, children, testid }) => (
  <section className="bg-card/90 border border-border rounded-sm p-5" data-testid={testid}>
    <h2 className="font-display text-xl uppercase mb-4">{title}</h2>
    {children}
  </section>
);

export default function WorkOrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [techs] = useApi("/technicians");
  const [inventory] = useApi("/inventory");
  const [settings] = useApi("/settings");
  const [msgOpen, setMsgOpen] = useState(false);

  const load = () => api.get(`/work-orders/${id}`).then((r) => setWo(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch, msg) => {
    try { const { data } = await api.put(`/work-orders/${id}`, patch); setWo(data); if (msg) toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };

  if (!wo) return <p className="text-muted-foreground">Loading…</p>;
  const tech = user?.shop_role === "technician";
  const locked = wo.status === "invoiced";
  const setField = (k) => (v) => setWo({ ...wo, [k]: v });
  const findParts = (part, pn) => {
    const v = wo.vehicle || {};
    const q = new URLSearchParams({ wo: wo.id, year: v.year || "", make: v.make || "", model: v.model || "", part: part || "", part_number: pn || "" });
    navigate(`/app/parts?${q}`);
  };
  const del = async () => {
    if (!window.confirm(`Delete work order #${wo.number}?`)) return;
    await api.delete(`/work-orders/${id}`); toast.success("Work order deleted"); navigate("/app/work-orders");
  };

  return (
    <div className="space-y-6">
      <Link to="/app/work-orders" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white" data-testid="wo-back-link"><ArrowLeft className="h-4 w-4" /> Work orders</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2" data-testid="wo-number">// Work order #{wo.number}{wo.invoice_number && ` · Invoice #${wo.invoice_number}`}</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold uppercase leading-none" data-testid="wo-vehicle-title">{wo.vehicle_label}</h1>
          <p className="text-sm text-muted-foreground mt-2">{typeLabel(wo.vehicle?.type)} · {wo.vehicle?.vin && `VIN ${wo.vehicle.vin} · `}{wo.customer?.name} {wo.customer?.phone && `· ${wo.customer.phone}`}</p>
        </div>
        {!tech && <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setMsgOpen(true)} className="rounded-sm hover:text-white" data-testid="wo-message-btn"><MessageSquare className="h-4 w-4 mr-1" /> AI customer message</Button>
          <Button asChild variant="outline" className="rounded-sm hover:text-white" data-testid="wo-invoice-btn"><Link to={`/app/work-orders/${wo.id}/print`}><FileText className="h-4 w-4 mr-1" /> {locked ? "Invoice" : "Estimate"} PDF</Link></Button>
          {!locked && <Button variant="ghost" onClick={del} className="text-muted-foreground hover:text-red-400" data-testid="wo-delete-btn"><Trash2 className="h-4 w-4" /></Button>}
        </div>}
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Select value={wo.status} onChange={(e) => save({ status: e.target.value }, e.target.value === "invoiced" ? "Invoiced — stock updated" : "Status updated")}
          options={STATUSES.filter((s) => !tech || s.key !== "invoiced" || locked).map((s) => ({ value: s.key, label: `Status: ${s.label}` }))} disabled={locked} data-testid="wo-status-select" />
        <Select value={wo.technician_id || ""} onChange={(e) => save({ technician_id: e.target.value || null }, "Technician assigned")} placeholder="Unassigned tech"
          options={(techs || []).map((t) => ({ value: t.id, label: `Tech: ${t.name}` }))} disabled={locked} data-testid="wo-tech-select" />
        {locked && <p className="flex items-center gap-2 text-sm text-violet-300"><Lock className="h-4 w-4" /> Invoiced and locked</p>}
      </div>

      <Panel title="3 C's — complaint, cause, correction" testid="wo-ccc-panel">
        <div className="grid lg:grid-cols-3 gap-4">
          {["complaint", "cause", "correction"].map((k) => (
            <label key={k} className="space-y-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</span>
              <textarea rows={5} value={wo[k]} disabled={locked} onChange={(e) => setField(k)(e.target.value)} onBlur={() => save({ [k]: wo[k] })}
                className={`${inputCls} h-auto py-2`} data-testid={`wo-${k}-input`} />
            </label>
          ))}
        </div>
        {wo.notes && <pre className="mt-4 whitespace-pre-wrap text-xs text-muted-foreground font-mono" data-testid="wo-notes">{wo.notes}</pre>}
      </Panel>

      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <Panel title="Labor" testid="wo-labor-panel">
            <LaborEditor lines={wo.labor_lines} setLines={setField("labor_lines")} locked={locked} laborRate={settings?.labor_rate || 0} hidePrices={tech}
              commit={(lines) => save({ labor_lines: lines || wo.labor_lines })} />
          </Panel>
          <Panel title="Parts" testid="wo-parts-panel">
            <PartsEditor lines={wo.part_lines} setLines={setField("part_lines")} locked={locked} inventory={inventory || []} markup={settings?.parts_markup || 0} hidePrices={tech}
              commit={(lines) => save({ part_lines: lines || wo.part_lines })} onFind={findParts} />
          </Panel>
        </div>
        <div className="space-y-6">
          {!tech && <Panel title="Totals" testid="wo-totals-panel">
            {[["Labor", wo.labor_total], ["Parts", wo.parts_total], [`Tax (${settings?.tax_rate ?? 0}% parts)`, wo.tax]].map(([l, v]) => (
              <p key={l} className="flex justify-between text-sm py-1"><span className="text-muted-foreground">{l}</span><span className="font-mono">{money(v)}</span></p>
            ))}
            <p className="flex justify-between items-end border-t border-border mt-2 pt-3"><span className="font-mono text-xs uppercase tracking-widest">Total</span>
              <span className="font-display text-4xl font-bold text-primary" data-testid="wo-total">{money(wo.total)}</span></p>
          </Panel>}
          <Panel title="Time clock" testid="wo-time-panel"><TimePanel wo={wo} techs={techs || []} reload={load} myTech={user?.technician_id} /></Panel>
        </div>
      </div>
      <MessageDialog open={msgOpen} onOpenChange={setMsgOpen} wo={wo} />
    </div>
  );
}
